import Vue from 'vue';
import Vuex from 'vuex';
// import router from '@/router';
import createPersistedState from 'vuex-persistedstate';
import firebase from 'firebase/app'
import 'firebase/auth'
import { vuexfireMutations, firestoreAction } from 'vuexfire';
import { db } from '@/main';

// modules
import auth from '@/store/auth';
import stripe from '@/store/stripe';

Vue.use(Vuex)

// the collections data is stored in
import { references } from '@/main'

const defaultState = {
  isLoading: false,
  introVideos: [], // NEW
  outroVideos: [], // NEW
  contentVideos: [],
  defaultTemplates: [],
  customTemplates: [],
  patientsList: [],
  selectedTemplate: null,
  builderMenuType: null, // controls which buttons/methods are available
  hasPlanEdited: false,
  
}

export default new Vuex.Store({
  plugins: [createPersistedState({
    // we are explicitly excluding auth.practice from persistedState, since user status's weren't reliably changing live
    paths: ['contentVideos', 'introVideos', 'outroVideos', 'defaultTemplates', 'auth.user'], // set what we want to cache. we ignore patients and templates so we can fetch live
    storage: window.sessionStorage, // TODO: on logout be sure to add this ```sessionStorage.clear();```
  })],
  modules: {
    auth,
    stripe,
  },
  state: {
    isLoading: false,
    introVideos: [],
    outroVideos: [],
    contentVideos: [],
    defaultTemplates: [],
    customTemplates: [],
    patientsList: [],
    selectedTemplate: null,
    builderMenuType: null, // controls which buttons/methods are available
    hasPlanEdited: false,
  },
  getters: {
    getAllContent(state) {
      return {
        videos: state.contentVideos,
        templates: {
          custom: state.customTemplates,
          default: state.defaultTemplates
        }
      }
    },
  },
  mutations: {
    CLEAR_CONTENT_VIDEOS(state) {
      state.contentVideos = [];
    },
    SET_LOADING_STATE(state, value) {
      state.isLoading = value;
    },
    SET_BUILDER_BAR_TYPE(state, value) {
      state.builderMenuType = value;
    },
    SELECT_TEMPLATE(state, object) {
      state.hasPlanEdited = false; // for tracking if a plan has been edited or not
      state.selectedTemplate = object;
    },
    NEW_TEMPLATE(state) { // triggers on "Create New Plan" (builderBarType == 'saveNew')
      state.selectedTemplate = {
        name: '',
        weeks: [ { 
          week: 0,
          playlists: [
            {
              name: 'Playlist',
              videos: [],
              notes: null,
              duration: 0,
              completion: 0,
              date: null,
            }
          ] 
        } ]
      };
    },
    INCREMENT_WEEK(state, add) { // add a week with an empty playlist, or remove a week
      state.hasPlanEdited = true; // mark the plan as edited
      if (add) { // add a week
        state.selectedTemplate.weeks.push({
          week: state.selectedTemplate.weeks.length,
          playlists: [ // by default we should have one playlist in a week
            {
              name: 'Playlist',
              videos: [],
              notes: null,
              duration: 0,
              completion: 0,
              date: null,
            }
          ],
        })
      } else { // delete a week
        if (state.selectedTemplate.weeks.length === 1) return // prevent deleting the only week
        else state.selectedTemplate.weeks.pop()
      }
    },
    INCREMENT_PLAYLIST(state, { value, week, playlist }) { // add another playlist to an existing week
      state.hasPlanEdited = true;
      if (value) {
        state.selectedTemplate.weeks[week].playlists.push({
          name: 'Playlist',
          videos: [],
          notes: null,
          duration: 0,
          completion: 0,
          date: null,
        })
      } else {
        if (state.selectedTemplate.weeks[week].playlists.length === 1) {
          return
        } else {
          state.selectedTemplate.weeks[week].playlists.splice(playlist, 1)
        }
      }
    },
    COPY_PLAYLIST(state, object) { // given a week index and a playlist, we push to that array // ONLY DUPLICATED PLAYLISTS HAVE A KEY
      state.hasPlanEdited = true;
      // re creating object from string and setting unique key prevents duplicates from staying in sync
      let parsed = JSON.parse(object)
      parsed.playlist.key = `${Date.now()}_${Math.random().toString(36).substr(2, 8)}`;
      state.selectedTemplate.weeks[parsed.week].playlists.push(parsed.playlist)
    },
    UPDATE_PLAYLIST(state, { week, playlist }) {
      state.hasPlanEdited = true;
      state.selectedTemplate.weeks[week].playlists[playlist.idx] = playlist.data;
    },
    RENAME_PLAN(state, name) {
      state.hasPlanEdited = true;
      state.selectedTemplate.name = name;
    },
    RESET(state) {
      Object.keys(defaultState).forEach(key => {
        state[key] = defaultState[key];
      });
    },
    ...vuexfireMutations,
  },
  actions: {
    async firstBind({ state, commit }) { // after clicking on 'proceed to dashboard' we verify the user is logged in, then bind firebase
      if (firebase.auth().currentUser) {
        try {
          commit('SET_LOADING_STATE', true)
          // dispatch('bindCustomTemplates', practice) // we bind so that we can fetch customTemplates in real time
          // dispatch('bindPatientsList', practice)

          // NEW
          // intros
          await db.collection('videos').where('type', '==', 'intro').get()
            .then((querySnapshot) => {
              let content = [];
              querySnapshot.forEach((doc) => {
                content.push(doc.data())
              });
              state.introVideos = content;
            })
            .catch((error) => {
              console.log("Error getting intro videos: ", error);
            });
          // outros
          await db.collection('videos').where('type', '==', 'outro').get()
            .then((querySnapshot) => {
              let content = [];
              querySnapshot.forEach((doc) => {
                content.push(doc.data())
              });
              state.outroVideos = content;
            })
            .catch((error) => {
              console.log("Error getting outro videos: ", error);
            });

          // contentVideos
          await db.collection('videos').where('type', '==', 'content').get()
            .then((querySnapshot) => {
              let content = [];
              querySnapshot.forEach((doc) => {
                content.push(doc.data())
              });
              // TODO: shouldn't be commiting mutations inside an action
              state.contentVideos = content;
            })
            .catch((error) => {
              console.log("Error getting contentVideos: ", error);
            });
          // default templates
          await db.collection('protocolTemplates').get()
            .then((querySnapshot) => {
              let content = [];
              querySnapshot.forEach((doc) => {
                content.push(doc.data())
              });
              state.defaultTemplates = content;
            })
            .catch((error) => {
              console.log("Error getting protocolTemplates: ", error);
            });
        } catch(err) {
          console.log(err)
        } finally {
          commit('SET_LOADING_STATE', false)
        }
      } else return
    },
    
    bindCustomTemplates: firestoreAction(async ({ bindFirestoreRef, commit }, practice) => {
      try {
        commit('SET_LOADING_STATE', true);
        // await bindFirestoreRef('customTemplates', db.collection('TherapistAccounts').doc(email).collection('customTemplates'))
        await bindFirestoreRef('customTemplates', db.collection(references.practice).doc(practice).collection('customTemplates'))
      } catch(err) {
        console.log(err)
        this.$buefy.dialog.alert({
          title: 'Error',
          message: err,
          type: 'is-danger',
          hasIcon: true,
          icon: 'times-circle',
          iconPack: 'fa',
          ariaRole: 'alertdialog',
          ariaModal: true
        })
      } finally {
        commit('SET_LOADING_STATE', false)
      }
      return;
    }),
    bindPatientsList: firestoreAction(({ bindFirestoreRef }, practice) => {
      // return bindFirestoreRef('patientsList', db.collection('TherapistAccounts').doc(email).collection('patients').orderBy('createdAt'));
      return bindFirestoreRef('patientsList', db.collection(references.practice).doc(practice).collection('patients').orderBy('createdAt'));
    }),
    
    unbindAllFirestore: firestoreAction(({ unbindFirestoreRef, commit }) => {
      unbindFirestoreRef('customTemplates')
      unbindFirestoreRef('patientData')
      commit('CLEAR_CONTENT_VIDEOS')
      sessionStorage.clear();
    }),
  },
})
